import React, { useEffect, useMemo, useState } from 'react';
import { DeleteIcon, ErrorCircleIcon } from 'tdesign-icons-react';
import {
  Button,
  Drawer,
  Link,
  Loading,
  MessagePlugin,
  Popconfirm,
  Space,
  Tabs,
  Tag,
  Tooltip,
} from 'tdesign-react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { useMemoizedFn } from 'ahooks';
import TabPanel from 'tdesign-react/es/tabs/TabPanel';
import dayjs from 'dayjs';
import ModalHeader from '@/components/Header/Modal';
import { fetchTopicDetailOverview, resetDetail } from '@/slices/topicSlice';
import { delTopic, doUnload } from '@/services/topicService';
import DisableBuriedPermWrapper from '@/components/DisableWrapper/DisableBuriedPermWrapper';
import { deal70101 } from '@/utils/common';
import Summary from './Summary';
import BasicPanel from './BasicPanel';
import AddPartionModal from './BasicPanel/AddPartionModal';
import Partition from './Partition';
import PermissionPanel from './PermissionPanel';
import SchemaPanel from './SchemaPanel';
import ConfigPanel from './ConfigPanel';
import Subscription from './Subscription';
import MessagePanel from './MessagePanel';
import StoragePanel from './StoragePanel';
import GeoPanel from './GeoPanel';

function Detail({
  loading,
  topic,
  clusterName,
  fetchTopicDetailOverview,
  overview,
  onRefresh,
  resetDetail,
  syncInfo,
}) {
  const [visible, setVisible] = useState(false);
  const [currentTab, setCurrentTab] = useState(1);
  const [visiblePartion, setVisiblePartion] = useState(false);

  const loadDetail = useMemoizedFn(async () => {
    fetchTopicDetailOverview({
      clusterName,
      topic,
    });
  });

  useEffect(() => {
    if (visible && currentTab === 1) {
      loadDetail();
    }

    if (!visible) {
      resetDetail();
      setCurrentTab(1);
    }
  }, [loadDetail, visible, resetDetail, currentTab]);

  const handleDelete = async () => {
    const { meta } = await delTopic({
      clusterName,
      topic,
    });
    // 主集群创建的资源已在灾备集群存在
    if (meta.code === 70101) {
      deal70101(meta.publicMessage, handleDelete, {
        url: '/cluster/topic/list',
        topic,
      });
      return;
    }
    MessagePlugin.success('删除成功！');
    setVisible(false);
    onRefresh();
  };

  const onClose = () => setVisible(false);

  const commonProps = useMemo(
    () => ({
      clusterName,
      topic,
    }),
    [clusterName, topic],
  );

  const handleUnload = async () => {
    await doUnload({
      clusterName,
      topic,
    });
    MessagePlugin.success('操作成功！');
    loadDetail();
  };

  let errorSync;
  if (syncInfo.syncRecovery === false) {
    errorSync = (
      <Tooltip
        content={
          <>
            <div>主备集群元数据不一致</div>
            <div>失败原因：{syncInfo.message}</div>
            <div>更新时间：{dayjs(syncInfo.syncTime).format('YYYY-MM-DD HH:mm:ss')}</div>
          </>
        }
      >
        <ErrorCircleIcon size={16} color='var(--td-error-color)' />
      </Tooltip>
    );
  }

  const footer = (
    <Footer>
      <Space size='small'>
        {overview.topicPartitionCount > 0 && (
          <DisableBuriedPermWrapper resource='pulsar:topic:list:dilatation'>
            <Button onClick={() => setVisiblePartion(true)} theme='primary'>
              分区扩容
            </Button>
          </DisableBuriedPermWrapper>
        )}

        <Popconfirm
          onConfirm={handleUnload}
          content='手动触发Topic
            unload操作，该操作会在对应的broker中关闭topic，并重新加载topic，意味着生产者端和消费端会断开重连，客户端自动适配。
            适应Topic内部状态异常，例如无法消费等紧急恢复场景。'
        >
          <DisableBuriedPermWrapper resource='pulsar:topic:list:uninstall'>
            <Button theme='default' variant='outline'>
              手动卸载
            </Button>
          </DisableBuriedPermWrapper>
        </Popconfirm>
      </Space>
    </Footer>
  );

  return (
    <>
      <LinkContainer>
        {errorSync}
        <Link theme='primary' style={{ wordBreak: 'break-all' }} onClick={() => setVisible(true)}>
          {topic}
        </Link>
      </LinkContainer>
      <Drawer
        size={1200}
        destroyOnClose
        header={
          <ModalHeader
            tags={[
              clusterName,
              topic,
              <Tag
                theme='warning'
                variant='light'
                key={2}
                style={{
                  marginLeft: 10,
                  color: '#F49D0B',
                  background: 'rgba(244, 157, 11, 0.10)',
                }}
              >
                {overview.topicPartitionCount ? '分区' : '非分区'}
              </Tag>,
            ]}
            tail={
              <Popconfirm theme='danger' content='确定删除？' onConfirm={handleDelete}>
                <DisableBuriedPermWrapper resource='pulsar:topic:list:delete'>
                  <Button theme='danger' variant='outline' size='small' icon={<DeleteIcon />}>
                    删除
                  </Button>
                </DisableBuriedPermWrapper>
              </Popconfirm>
            }
          >
            Topic
          </ModalHeader>
        }
        visible={visible}
        onClose={onClose}
        footer={currentTab === 1 ? footer : null}
      >
        {visible && (
          <Loading loading={loading} size='small'>
            <Root>
              <Summary />

              <Tabs value={currentTab} onChange={setCurrentTab}>
                <TabPanel value={1} destroyOnHide label='概览'>
                  <BasicPanel commonProps={commonProps} onRefresh={loadDetail} />
                </TabPanel>
                {overview.topicPartitionCount > 0 && (
                  <TabPanel value={2} destroyOnHide label='分区'>
                    <Partition commonProps={commonProps} />
                  </TabPanel>
                )}
                <TabPanel value={3} destroyOnHide label='订阅'>
                  <Subscription commonProps={commonProps} namespace={overview.namespace} />
                </TabPanel>
                <TabPanel value={4} destroyOnHide label='消息'>
                  <MessagePanel commonProps={commonProps} />
                </TabPanel>
                <TabPanel value={5} destroyOnHide label='权限'>
                  <PermissionPanel clusterName={clusterName} topic={topic} />
                </TabPanel>
                <TabPanel value={6} destroyOnHide label='配置'>
                  <ConfigPanel commonProps={commonProps} />
                </TabPanel>
                <TabPanel value={7} destroyOnHide label='存储'>
                  <StoragePanel commonProps={commonProps} />
                </TabPanel>
                <TabPanel value={8} destroyOnHide label='SCHEMA'>
                  <SchemaPanel commonProps={commonProps} />
                </TabPanel>
                <TabPanel value={9} destroyOnHide label='GEO'>
                  <GeoPanel commonProps={commonProps} closeDrawer={onClose} />
                </TabPanel>
              </Tabs>
            </Root>
          </Loading>
        )}
      </Drawer>
      <AddPartionModal
        overview={overview}
        visible={visiblePartion}
        handleClose={() => setVisiblePartion(false)}
      />
    </>
  );
}

export default connect(
  (state) => ({
    overview: state.topic.detailOverview,
    loading: state.topic.loading,
  }),
  {
    fetchTopicDetailOverview,
    resetDetail,
  },
)(Detail);

const Root = styled.div`
  margin-top: -15px;
`;

const Footer = styled.div`
  padding-left: 10px;
`;

const LinkContainer = styled.div`
  display: flex;
  gap: 4px;
  .t-icon {
    flex-shrink: 0;
    height: 25px;
  }
`;
